import { useEffect, useRef } from 'react';
import {
  FUNDING,
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { useRouter } from 'next/router';
import { getLanguage, t } from 'react-switch-lang';
import { captureException, captureMessage } from '@sentry/react';
import { apiErrorHandled, completePayment, paymentCallback } from '../../utils/ApiClient';
import { DISPATCHERS, usePaymentProcessContext } from '../../context/PaymentProcessContext';
import { isDevelopment } from '../../utils/HostingEnv';
import { events, logAmpEvent } from '../../utils/Amplitude';
import Button from '../Button';
import { toErrorPage } from '../ErrorDisplay';
import Alert from '../Alert';

/**
 * See {@link https://github.com/Payment-Source/PaySimply/blob/ba5f06b27524330050c5d1f2e704b6bb7f9c7730/Partner/Review.aspx#L152-L170|reference}
 * @type {import('@paypal/react-paypal-js').ReactPayPalScriptOptions}
 */
const initialOptions = {
  // setting environment to 'sandbox' causes "zoid destroyed all components" error in development
  // when reactStrictMode is enabled (commenting out the line below fixes the issue)
  environment: isDevelopment ? 'sandbox' : 'production',
  // ClientID from https://github.com/Payment-Source/PSInet-Library/blob/06bd0f34d9dcaf067718656a09d07c2bc1a20416/Payments/modPayPal.vb#L186
  clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
  enableFunding: [FUNDING.CARD],
  disableFunding: [FUNDING.CREDIT],
  currency: 'CAD',
  dataPageType: 'checkout',
  commit: true,
  components: 'buttons',
};

export default function PayPal({ createOrder, onClick }) {
  return (
    <PayPalScriptProvider onError options={{ ...initialOptions, locale: `${getLanguage()}_CA` }}>
      <PayPalButton createOrder={createOrder} onClick={onClick} />
    </PayPalScriptProvider>
  );
}

export function payPalRedirectError(path) {
  return { redirect: path };
}

function PayPalButton({ createOrder, onClick }) {
  const {
    dispatchPaymentProcess,
    paymentProcess: { accountDetails, paymentCode, sessionToken },
  } = usePaymentProcessContext();
  const [{ isInitial, isPending, isRejected }] = usePayPalScriptReducer();
  const router = useRouter();

  const paymentCodeRef = useRef();
  useEffect(() => {
    paymentCodeRef.current = paymentCode;
  }, [paymentCode]);
  const sessionTokenRef = useRef();
  useEffect(() => {
    sessionTokenRef.current = sessionToken;
  }, [sessionToken]);

  if (isInitial || isPending) return <Button loading />;
  if (isRejected) return <Alert msg={t('Error_Default')} className="mb-0" />; // TODO: custom error message

  return (
    <PayPalButtons
      fundingSource="paypal"
      className="payPalButtonContainer"
      style={{
        layout: 'vertical',
        shape: 'rect',
        color: 'blue',
        label: 'paypal',
      }}
      /**
       * order of execution:
       * 1. PayPal button is clicked, PayPal popup opens
       * 2. onClick is called
       * 3. onClick executes reCAPTCHA
       *  - if reCAPTCHA challenge is triggered, onClick rejects and PayPal popup closes
       *    end of flow until user clicks the button again
       *  - if reCAPTCHA resolves with the token, onClick resolves (proceeds to step 4)
       * 3. createOrder is called
       *  - calls onSubmit, which executes reCAPTCHA again (resolves with token from previous step)
       *  - makes the BeginPayment API call (onSubmit) and returns the order ID
       * 4. user completes payment
       * 5. onApprove is called
       */
      onClick={onClick}
      createOrder={createOrder}
      onApprove={async (data) => {
        logAmpEvent(events.PAYPAL_PAYMENT_APPROVED);
        const res = await completePayment(sessionTokenRef.current, {
          Email: accountDetails.Email,
          PaymentOption: 'PayPal',
          PaymentCode: data.paymentID,
          PayerId: data.payerID,
          PayPalSeq: paymentCodeRef.current.PayPalSeq,
          Language: getLanguage(),
        });
        // TODO implement calling actions.restart() for recoverable INSTRUMENT_DECLINED error
        if (apiErrorHandled(res, (msg) => router.push(toErrorPage(msg)), router)) return;
        if (!res.df.transNo) {
          captureMessage('Empty transNo returned for PayPal CompletePayment API call.', (scope) => {
            scope.setTag('PayPalSeq', paymentCodeRef.current.PayPalSeq);
          });
          router.push(toErrorPage());
          return;
        }
        dispatchPaymentProcess(DISPATCHERS.COMPLETE_PAYMENT(res.df.transNo));
        router.push('/complete-payment');
        // the class name was the same on prod, even though it has the word 'sandbox' in it
        const payPalOverlay = document.querySelector('.paypal-checkout-sandbox');
        if (payPalOverlay) payPalOverlay.style.display = 'none';
      }}
      onError={(err) => {
        // check if payPalRedirectError was thrown
        if (err && err.redirect) {
          router.push(err.redirect);
          return;
        }
        logAmpEvent(events.PAYPAL_PAYMENT_ERROR);
        captureException(err);
        router.push(toErrorPage());
      }}
      onCancel={() => {
        logAmpEvent(events.PAYPAL_PAYMENT_CANCELLED);
        paymentCallback(sessionTokenRef.current, {
          PaymentOption: 'PayPal',
          PaymentCode: `${paymentCodeRef.current.PayPalSeq}`,
          Payload: 'cancel',
          Email: accountDetails.Email,
          Language: getLanguage(),
        }).then((res) => {
          apiErrorHandled(res, undefined, router);
          if (res.Result !== 80) router.push('/');
        });
      }}
    />
  );
}
